<template>
  <b-card
    body
    class="rounded-[8px] !shadow-none"
  >
    <validation-observer
      ref="informasiGudangForm"
      #default
    >
      <b-form
        @submit.prevent="submitForm"
      >
        <div class="d-flex items-center justify-content-between mb-[16px]">
          <h2 class="text-black font-semibold text-[20px] mb-0 flex items-center gap-[8px]">
            Fasilitas Gudang <b-spinner
              v-if="isLoading"
              small
            />
          </h2>
          <div class="flex items-center gap-[8px]">
            <b-button
              variant="outline-primary"
              size="sm"
              :disabled="onEditMode || isLoading"
              @click="onEditMode = !onEditMode"
            >
              Edit
            </b-button>
            <b-button
              type="submit"
              size="sm"
              class="!flex items-center gap-[8px]"
              :variant="onEditMode ? 'primary' : 'secondary'"
              :disabled="!onEditMode || isPending"
            >
              <b-spinner
                v-if="isPending"
                small
              />
              Simpan
            </b-button>
          </div>
        </div>

        <div class="flex items-start flex-col md:flex-row gap-[16px] w-full">
          <div class="w-full grow">
            <validation-provider
              #default="{ errors }"
              name="fasilitas"
            >
              <div class="rounded-[8px] border border-[#E2E2E2]">
                <div class="bg-[#F8F8F8] rounded-t-[8px] p-[16px]">
                  <p class="text-[#333333] font-semibold text-[14px] mb-0">
                    List Fasilitas
                  </p>
                </div>
                <div class="p-[16px]">
                  <b-form-checkbox-group
                    v-model="selectedFasilitas"
                    :options="fasilitasOptions"
                    :disabled="!onEditMode"
                    name="fasilitas"
                    label="Fasilitas"
                    class="flex flex-col flex-wrap gap-[12px] sm:max-h-[130px] [&_div:has(input:disabled)_label]:!text-[#333333]"
                  />
                </div>
                <small
                  v-if="errors[0]"
                  class="text-danger"
                >{{ errors[0] }}</small>
              </div>
            </validation-provider>
          </div>
          <div class="w-full grow">
            <validation-provider
              #default="{ errors }"
              name="supporting-services"
            >
              <div class="rounded-[8px] border border-[#E2E2E2]">
                <div class="bg-[#F8F8F8] rounded-t-[8px] p-[16px]">
                  <p class="text-[#333333] font-semibold text-[14px] mb-0">
                    Supporting Service
                  </p>
                </div>
                <div class="p-[16px]">
                  <b-form-textarea
                    v-model="supportingServices.description"
                    :disabled="!onEditMode"
                    rows="4"
                    name="supporting-services"
                    label="Supporting Service"
                    class="!text-[#333333] border border-[#E2E2E2] disabled:!bg-[#F4F4F4] !py-[10px] min-h-[117px]"
                    style="border-color: #E2E2E2!important;"
                  />
                </div>
                <small
                  v-if="errors[0]"
                  class="text-danger"
                >{{ errors[0] }}</small>
              </div>
            </validation-provider>
          </div>
        </div>
      </b-form>
    </validation-observer>

    <modal-general-action
      ref="mgac_kompack"
      :confirm-disabled="eligibleForColdStorage && !biayaColdStorage"
    >
      <div
        v-if="eligibleForColdStorage"
        class="bg-[#F8F8F8] rounded-[8px] p-[16px] pb-0 border"
      >
        <p>Kamu mengaktifkan fasilitas cold storage. Yuk ajukan tarif penyimpanan cold storage di gudang kamu</p>
        <ReusableInput
          v-model="biayaColdStorage"
          name="Biaya Cold Storage"
          placeholder="Masukkan biaya cold storage"
          rules="required"
          currency
        >
          <template #input="{ value, disabled, onInput, onBlur, onFocus, dataId, type, placeholder }">
            <b-input-group>
              <b-form-input
                :id="dataId"
                :value="value"
                :placeholder="placeholder"
                :disabled="disabled"
                :type="type"
                class="!text-[#333333] border disabled:!bg-[#F4F4F4] !py-[10px]"
                style="border-color: #E2E2E2!important;"
                @input="onInput($event)"
                @blur="onBlur($event)"
                @focus="onFocus($event)"
              />
              <b-input-group-append>
                <b-input-group-text
                  class="!border-[#E2E2E2]"
                  :class="{'!bg-[#F4F4F4]': !onEditMode}"
                >
                  m³
                </b-input-group-text>
              </b-input-group-append>
            </b-input-group>
          </template>
        </ReusableInput>
      </div>
    </modal-general-action>

    <modal-success-change-cold-storage-fee ref="mssccs_kompack" />
  </b-card>
</template>

<script>
import { toastError, toastSuccess } from '@/libs/toastification'
import { ValidationObserver, ValidationProvider } from 'vee-validate'
import { fasilitasOptions } from '../config'
import ModalGeneralAction from './ModalGeneralAction.vue'
import ReusableInput from './Input.vue'
import ModalSuccessChangeColdStorageFee from './ModalSuccessChangeColdStorageFee.vue'

/**
 * CardFasilitasGudang
 * @description CardFasilitasGudang is a component
 * that displays, edit and delete the facilities of the warehouse facilities detail
 * @param {Number} warehouseId - The id of the warehouse
 * @returns {Object} CardFasilitasGudang
 */
export default {
  name: 'CardFasilitasGudang',
  components: {
    ValidationObserver,
    ValidationProvider,
    ModalGeneralAction,
    ReusableInput,
    ModalSuccessChangeColdStorageFee,
  },
  props: {
    warehouseId: {
      type: Number,
      required: true,
    },
  },
  data() {
    return {
      isLoading: true,
      isPending: false,
      onEditMode: false,
      selectedFasilitas: [],
      supportingServices: {
        id: 0,
        description: '',
      },
      fasilitasOptions,
      biayaColdStorage: undefined,
    }
  },
  computed: {
    eligibleForColdStorage() {
      return this.selectedFasilitas.includes(2) // cold storage id
    },
  },
  watch: {
    /**
     * Watch the warehouse id and fetch the warehouse facilities
     */
    warehouseId: {
      handler(newVal) {
        if (newVal >= 0) {
          this.fetchFasilitasGudang()
        }
      },
    },
  },
  methods: {
    /**
     * Validate the form, take user confirmation and submit the data
     */
    submitForm() {
      this.isPending = true
      this.$refs.informasiGudangForm.validate().then(success => {
        if (success) {
          const mappedFasilitas = {
            facility_ids: this.selectedFasilitas,
            supporting_services: this.supportingServices,
          }

          this.$refs.mgac_kompack.show({
            title: 'Konfirmasi perubahan data fasilitas gudang',
            message: 'Pastikan data yang kamu ubah sudah benar',
            confirmText: 'Simpan',
            onConfirm: ({ hide: closeModal, setIsLoading }) => {
              setIsLoading(true)
              this.mutateFasilitasGudang(mappedFasilitas).then(async () => {
                this.fetchFasilitasGudang()
                setIsLoading(false)
                if (this.eligibleForColdStorage) {
                  await this.fnMutateCost({
                    service: 4, // 4 = cold storage
                    price: Number(this.biayaColdStorage),
                    errorMessage: 'Gagal mengubah data biaya cold storage, silahkan refresh untuk coba lagi',
                  })
                  this.$refs.mssccs_kompack.show()
                }
                closeModal()
              })
            },
          })
        }
      }).finally(() => {
        this.isPending = false
      })
    },
    /**
     * Fetch warehouse facilities and map the data to the component
     */
    async fetchFasilitasGudang() {
      try {
        const { data: result } = await this.$http_new.get(`/komship/api/v1/warehouse-facilities/${this.warehouseId}/warehouses`)
        const { data } = result

        this.selectedFasilitas = data.facilities.map(facility => facility.id)
        this.supportingServices = data.supporting_services
        this.$emit('update:facilities', data.facilities)
      } catch (error) {
        toastError('Gagal mengambil data fasilitas gudang, silahkan refresh untuk coba lagi')
      } finally {
        this.isLoading = false
      }
    },
    /**
     * Mutate warehouse facilities and submit the data
     * @param {Object} params - The parameters
     * @param {Number} params.facility_ids - The facility ids
     * @param {Object} params.supporting_services - The supporting services
     */
    async mutateFasilitasGudang({
      facility_ids,
      supporting_services,
    }) {
      this.isPending = true
      try {
        await this.$http_new.put(`/komship/api/v1/warehouse-facilities/${this.warehouseId}/warehouses`, {
          facility_ids,
          supporting_services,
        })
        toastSuccess('Data fasilitas gudang berhasil diubah')
        this.onEditMode = false
      } catch (error) {
        toastError('Gagal mengubah data fasilitas gudang, silahkan refresh untuk coba lagi')
      } finally {
        this.isPending = false
      }
    },
    /**
     * Mutate cost and submit the data. For submit data, the submission status will be constant of 1 (diajukan)
     * @param {Object} params - The parameters
     * @param {Number} params.service - The service: 1 = penyimpanan, 2 = jasa packing, 3 = print label, 4 = cold storage
     * @param {Number} params.price - The price
     * @param {Number} params.submission_status - The submission status(would be constant of 1 (diajukan)): 1 = diajukan(default), 2 = disetujui, 3 = ditolak
     * @param {String} params.errorMessage - The error message
     * @returns {Promise<boolean>}
     */
    async fnMutateCost({ service, price, errorMessage = 'Gagal mengubah data biaya gudang, silahkan refresh untuk coba lagi' }) {
      try {
        await this.$http_new.post('/komship/api/v1/warehouse-cost-submissions', {
          service,
          price,
          submission_status: 1, // 1 = diajukan
        })
        this.$emit('updated:facilities-cost')
      } catch (error) {
        toastError(errorMessage)
      }
    },
  },
}
</script>
